// @ts-check

import { configureStore } from "@reduxjs/toolkit";
import { GiCardboardBox } from "react-icons/gi";
import { QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import "./App.less";
import { AppContextProvider } from "./AppContext";
import SignalMap from "./components/SignalMap";
import ConnectionDashboard from "./containers/ConnectionDashboard";
import ControlCenter from "./containers/ControlCenter";
import { Dashboard2 } from "./containers/Dashboard2";
import DeviceManagement from "./containers/DeviceManagement";
import GardensList2 from "./containers/GardensList-2";
import Login from "./containers/Login";
import Scheduler2 from "./containers/Scheduler-2";
import Shifts from "./containers/Shifts";
import SignalDashboard from "./containers/SignalDashboard";
import WorkOrders from "./containers/WorkOrders";
import { AuditProvider } from "./contexts/audit-context";
import DashboardProvider from "./contexts/dashboard-context";
import { DeviceProvider } from "./contexts/device-context";
import { EventSchedulerProvider } from "./contexts/event-scheduler-context";
import GardenProvider from "./contexts/garden-context";
import { OptimizationProvider } from "./contexts/optimization-context";
import { PlansProvider } from "./contexts/plans-context";
import rootReducer from "./redux/slices";
import Audit from "./system-management/Audit";
import Audit2 from "./system-management/Audit2";
import { Devices2 } from "./system-management/Devices2";
import GardenAccess from "./system-management/GardenAccess";
import Tags from "./system-management/Tags";
import UserManagement from "./system-management/UserManagement";
import UserAccess from "./system-management/UsersAccess";
import { debugQueryClient, getQueryClient } from "./utils/QueryClient";
import { QueryDevtools } from "./utils/QueryDevtools";

// const queryClient = new QueryClient();
const queryClient = getQueryClient(); // Experimental: Use singleton QueryClient instance

// Add debugging in development
if (process.env.NODE_ENV === "development") {
  debugQueryClient(queryClient);
}

const store = configureStore({
  reducer: rootReducer,
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <AppContextProvider>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="control-center" element={<ControlCenter />}>
              <Route
                path="gardens"
                element={
                  <GardenProvider>
                    <DeviceProvider>
                      <GardensList2 />
                    </DeviceProvider>
                  </GardenProvider>
                }
              />

              <Route
                path="dashboard"
                element={
                  <DashboardProvider>
                    <Dashboard2 />
                  </DashboardProvider>
                }
              />

              <Route
                path="gardens/:id"
                element={
                  <GardenProvider>
                    <DeviceProvider>
                      <OptimizationProvider>
                        <PlansProvider>
                          <EventSchedulerProvider>
                            <Scheduler2 />
                          </EventSchedulerProvider>
                        </PlansProvider>
                      </OptimizationProvider>
                    </DeviceProvider>
                  </GardenProvider>
                }
              />

              <Route
                path="work-orders"
                element={
                  <GardenProvider>
                    <WorkOrders />
                  </GardenProvider>
                }
              />

              <Route path="shifts" element={<Shifts />} />

              <Route path="connection-dashboard" element={<ConnectionDashboard />} />

              <Route path="signal-dashboard" element={<SignalDashboard />} />

              <Route path="users" element={<UserManagement />}>
                <Route path="user-access" element={<UserAccess />} />

                <Route path="garden-access" element={<GardenAccess />} />

                <Route path="tags" element={<Tags />} />

                <Route
                  path="audit"
                  element={
                    <GardenProvider>
                      <AuditProvider>
                        <Audit2 />
                      </AuditProvider>
                    </GardenProvider>
                  }
                />

                <Route path="audit/old" element={<Audit />} />

                <Route
                  path="devices"
                  element={
                    <GardenProvider>
                      <DeviceProvider>
                        <Devices2 />
                      </DeviceProvider>
                    </GardenProvider>
                  }
                />

                <Route index element={<Navigate to="garden-access" />} />
              </Route>

              <Route path="devices" element={<DeviceManagement />} />

              <Route index element={<Navigate to="gardens" />} />
            </Route>

            <Route path="signal-map" element={<SignalMap />}></Route>

            <Route index element={<Login />} />

            <Route
              path="*"
              element={
                <div className="h-screen w-screen flex items-center justify-center">
                  <div className="flex flex-col gap-2 items-center">
                    <div className="text-5xl flex">
                      <GiCardboardBox /> 404
                    </div>
                    Page not found
                  </div>
                </div>
              }
            />
          </Routes>
        </AppContextProvider>
      </Provider>
      <QueryDevtools />
    </QueryClientProvider>
  );
}

export default App;
